import { RadioGroup } from '@headlessui/react'
import { Button } from '@progress/kendo-react-buttons'
import {
    DropDownList,
    ListItemProps,
} from '@progress/kendo-react-dropdowns'
import DataTable from 'components/DataTable'
import { createModal } from 'components/Modals/ModalFactory'
import { ModalWrapper } from 'components/Modals/ModalWrapper'
import { OptionListItem } from 'components/OptionList'
import Translation from 'components/Translation'
import { AuthContext } from 'context/AuthContext'
import { useRouter } from 'next/router'
import { cloneElement, useState } from 'react'
import { DEFAULT_LOCATION } from 'types/membership'
import { ServiceApiType, ServiceType } from 'types/service'
import { classNames } from 'utils/dom-helpers'

const ModalProvider = createModal(
    AuthContext,
    'AppointmentAddModal',
    () => (
        <>
            <i className="feather feather-plus mr-4" />
            <Translation
                render_as="span"
                className="circular"
                content_key="add_appointment"
            />
        </>
    ),
    () => (
        <span className="inline-block w-6 h-6 text-primary text-center font-extralight">
            <i className="feather feather-x " />
        </span>
    )
)

export const AppointmentAddModalOpener = ModalProvider.Opener
export const AppointmentAddModalCloser = ModalProvider.Closer

function getDateFromYmd(dt: string) {
    const ymd = dt as unknown as string
    const [y, m, d] = ymd.split('-') as unknown[] as number[]
    return new Date(y, m - 1, d, 0, 0, 0, 0)
}

export function AppointmentAddModal() {
    const router = useRouter()

    const [service_type, setServiceType] =
        useState<ServiceApiType>('APPOINTMENT')
    const serviceTypes = ['GROUP CLASS', 'APPOINTMENT']

    const [locations, setLocations] = useState<OptionListItem[]>([
        DEFAULT_LOCATION,
    ])
    const [location, setLocation] = useState()

    let active_week = new Date()
    let selected_date = new Date()
    if (router.query.d) {
        active_week = getDateFromYmd(router.query.d as unknown as string)
        selected_date = active_week
    }

    function LocationItemRender(
        li: React.ReactElement<HTMLLIElement>,
        item_props: ListItemProps
    ) {
        const { dataItem } = item_props
        const item_children = (
            <div className="m-2 p-2">
                <p className="text-base font-semibold">{dataItem.text}</p>
            </div>
        )

        return cloneElement(li, li.props, item_children)
    }

    function LocationValueRender(
        element: React.ReactElement<HTMLSpanElement>,
        value: any
    ) {
        if (!value) {
            return element
        }

        const children = [
            <div key={1} className="px-4">
                {value.text}
            </div>,
        ]

        return cloneElement(element, { ...element.props }, children)
    }

    function RenderAppointments(props: any) {
        const appointments: any[] = props.group_class
        const rows: HTMLTableRowElement[] = (appointments || []).map(
            ({ id, time, duration, location, price, staff }: any, idx) =>
                (
                    <tr
                        key={id}
                        className={classNames(
                            idx % 2
                                ? ''
                                : 'bg-primary-lighter bg-opacity-30',
                            'hover:bg-secondary hover:bg-opacity-10'
                        )}
                    >
                        <td className="px-5 py-4 align-top whitespace-nowrap">
                            <span className="block">{time}</span>
                            <span className="block">{duration}</span>
                        </td>
                        <td className="px-5 py-4 align-top whitespace-nowrap">
                            <span>{location}</span>
                        </td>
                        <td className="px-5 py-4 align-top whitespace-nowrap">
                            <span>{price}</span>
                        </td>
                        <td className="px-5 py-4 align-top whitespace-nowrap">
                            <span>{staff}</span>
                        </td>
                        <td className="px-5 py-4 align-top whitespace-nowrap">
                            <Button className="rounded-full text-xs uppercase tracking-widest bg-primary text-white lg:py-2 lg:mt-0 px-3 py-4 mt-4 lg:w-max w-full">
                                Book Now
                            </Button>
                        </td>
                    </tr>
                ) as unknown as HTMLTableRowElement
        )

        return (
            <div className="align-middle inline-block min-w-full">
                <div className="overflow-hidden mt-4">
                    <DataTable
                        rows={rows}
                        columns={[
                            {
                                label: 'Time & Duration',
                                classNames:
                                    'px-5 py-3 text-left text-xs text-gray-500 uppercase tracking-wider',
                            },
                            {
                                label: 'Location',
                                classNames:
                                    'px-5 py-3 text-left text-xs text-gray-500 uppercase tracking-wider',
                            },
                            {
                                label: 'Price',
                                classNames:
                                    'px-5 py-3 text-left text-xs text-gray-500 uppercase tracking-wider',
                            },
                            {
                                label: 'Instructor',
                                classNames:
                                    'px-5 py-3 text-left text-xs text-gray-500 uppercase tracking-wider',
                            },
                            {
                                classNames:
                                    'px-5 py-3 text-left text-xs text-gray-500 uppercase tracking-wider',
                            },
                        ]}
                    />
                </div>
            </div>
        )
    }
    const group_class = [
        {
            time: '8:30PM-9:30PM',
            duration: '60 mins',
            is_recurring: false,
            location: 'Tai Seng',
            price: 'SGD 28.00',
            staff: 'Arlene McCoy',
        },
    ]

    return (
        <ModalProvider.Visible>
            <ModalWrapper className="w-auto">
                <div className="bg-white shadow-2xl overflow-x-hidden sm:rounded-md relative border-2 border-gray-100 my-auto">
                    <div className="flex px-4 text-gray-500 z-10 w-full pt-4 bg-opacity-30 pb-3 bg-primary-lighter">
                        <div className="text-lg text-primary-dark">
                            <span className="circular ml-3">
                                <Translation content_key="add_appointment" />
                            </span>
                            <AppointmentAddModalCloser className="self-center pt-1" />
                        </div>
                    </div>
                    <div className="flex flex-col gap-y-3 w-full p-10 text-black">
                        <div className="flex flex-row gap-10">
                            <RadioGroup
                                value={service_type}
                                onChange={(v) => {
                                    setServiceType(v)
                                }}
                            >
                                <RadioGroup.Label
                                    className={classNames('text-lg block')}
                                >
                                    <Translation content_key="service_type_label" />
                                </RadioGroup.Label>
                                <div className="flex gap-6">
                                    {serviceTypes.map((v, idx) => {
                                        return (
                                            <RadioGroup.Option
                                                key={v}
                                                value={v}
                                            >
                                                {({ checked }) => (
                                                    <div className="flex items-center gap-2 text-base cursor-pointer">
                                                        <div
                                                            className={classNames(
                                                                checked
                                                                    ? 'border-primary-light'
                                                                    : 'border-gray-400',
                                                                'rounded-full w-6 h-6 border-2 flex items-center'
                                                            )}
                                                        >
                                                            <i
                                                                className={classNames(
                                                                    checked
                                                                        ? 'bg-primary-light'
                                                                        : '',
                                                                    'rounded-full w-4 h-4 block m-auto'
                                                                )}
                                                            />
                                                        </div>
                                                        <span
                                                            className={
                                                                checked
                                                                    ? 'text-primary-light'
                                                                    : ''
                                                            }
                                                        >
                                                            {
                                                                Object.values(
                                                                    ServiceType
                                                                )[idx]
                                                            }
                                                        </span>
                                                    </div>
                                                )}
                                            </RadioGroup.Option>
                                        )
                                    })}
                                </div>
                            </RadioGroup>
                        </div>

                        <span>Book a Class</span>

                        <div className="flex flex-row justify-between">
                            <span>Wednesday, November 24, 2021</span>
                            <DropDownList
                                className="bg-white py-2 mt-1 block w-48 shadow-sm border border-gray-300 rounded-md text-base"
                                iconClassName="feather-chevron-down"
                                data={(locations as OptionListItem[]) || []}
                                value={location}
                                textField="text"
                                dataItemKey="value"
                                required={true}
                                itemRender={LocationItemRender}
                                valueRender={LocationValueRender}
                                onChange={(e) => {
                                    setLocation(e.target.value)
                                }}
                                popupSettings={{
                                    className: "z-[102]"
                                }}
                            />
                        </div>

                        <RenderAppointments group_class={group_class} />
                    </div>
                </div>
            </ModalWrapper>
        </ModalProvider.Visible>
    )
}
